import {
  archiveWorkOrdersBatch,
  type WorkOrderBatchActionIssue,
} from "../../../../../../../../../../../api/kehrblatt/work-orders"

export type ArchiveCreatedWorkOrdersResult = {
  resolvedIds: string[]
  unresolvedIds: string[]
  issues: WorkOrderBatchActionIssue[]
}

const ARCHIVE_BATCH_SIZE = 500

const archiveChunk = async (ids: string[]): Promise<ArchiveCreatedWorkOrdersResult> => {
  try {
    const result = await archiveWorkOrdersBatch(ids, "USER_REQUEST")
    const issues = new Map(
      [...result.skipped, ...result.failed].map((issue) => [issue.work_order_id, issue]),
    )
    const failed = new Set(result.failed.map((issue) => issue.work_order_id))
    const processed = new Set(result.action === "archive" ? result.processed_work_order_ids : [])
    const resolvedIds = ids.filter(
      (id) =>
        result.action === "archive" &&
        !failed.has(id) &&
        (issues.has(id) ? issues.get(id)?.reason_code === "already_archived" : processed.has(id)),
    )
    const resolved = new Set(resolvedIds)
    const unresolvedIds = ids.filter((id) => !resolved.has(id))

    return {
      resolvedIds,
      unresolvedIds,
      issues: unresolvedIds.map(
        (id) =>
          issues.get(id) ?? {
            work_order_id: id,
            reason_code: "unconfirmed",
            message: "Die Archivierung dieses Auftrags wurde nicht bestätigt.",
          },
      ),
    }
  } catch {
    // A lost response does not prove rollback. Retry the same unresolved IDs.
    return {
      resolvedIds: [],
      unresolvedIds: ids,
      issues: ids.map((id) => ({
        work_order_id: id,
        reason_code: "request_failed",
        message: "Die Archivierung konnte nicht bestätigt werden. Bitte erneut versuchen.",
      })),
    }
  }
}

/** Only confirmed per-order outcomes finish cancellation; requests stay within the API limit. */
export const archiveCreatedWorkOrders = async (
  workOrderIds: string[],
): Promise<ArchiveCreatedWorkOrdersResult> => {
  const ids = [...new Set(workOrderIds)]
  const result: ArchiveCreatedWorkOrdersResult = { resolvedIds: [], unresolvedIds: [], issues: [] }
  for (let offset = 0; offset < ids.length; offset += ARCHIVE_BATCH_SIZE) {
    const chunk = await archiveChunk(ids.slice(offset, offset + ARCHIVE_BATCH_SIZE))
    result.resolvedIds.push(...chunk.resolvedIds)
    result.unresolvedIds.push(...chunk.unresolvedIds)
    result.issues.push(...chunk.issues)
  }

  return result
}
